import { Service, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { type Observable, tap } from 'rxjs';
import { environment } from '@environments/environment';
import type { PanelEntryDeleteResponse, PanelListResponse } from '../models/panel-response.model';
import { PanelSnapshotStore } from '../stores/panel-snapshot.store';
import { panelParams } from '../utils/panel-params.util';

/**
 * Endpoint panel profil vendor (7 sub-halaman profil). Stateless — tidak menyimpan apa pun;
 * hasilnya dinormalisasi & disimpan `ProfileCompletenessStore`. Satu-satunya efek samping:
 * `getPanel()` menyalin response terakhirnya ke `PanelSnapshotStore` (lihat di sana kenapa).
 *
 * Tanpa `withCredentials` / handle 401 / toast error — semuanya sudah ditangani interceptor
 * global (docs/recipes/http-service.md). Bukan pula `DataListService`: ini bukan tabel
 * ter-paginasi, tidak ada filter, dan pagingnya justru DIMATIKAN.
 *
 * Lihat plans/vendors/profile/PLAN-PROFILE-TAB.md §11.
 */
@Service()
export class ProfilePanelService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api`;
  private readonly snapshots = inject(PanelSnapshotStore);

  /**
   * Ambil SELURUH baris satu panel.
   *
   * Query param-nya datang dari `panelParams()` — helper yang sama dipakai endpoint DETAIL
   * (`ProfileOfficeService` / `ProfileGeneralService`), supaya bentuk request keduanya tidak bisa
   * berbeda. Alasan tiap param ada di sana.
   *
   * @param endpoint path relatif dari `PanelDef.endpoint`, mis. `vendor/registration/profile_deeds`
   * @param vendorId `'current_user'` (situs vendor) atau UUID vendor (situs admin)
   */
  getPanel(endpoint: string, vendorId: string): Observable<PanelListResponse> {
    return this.http
      .get<PanelListResponse>(`${this.base}/${endpoint}`, { params: panelParams(vendorId) })
      .pipe(tap((res) => this.snapshots.set(endpoint, vendorId, res.data)));
  }

  /**
   * Hapus satu baris panel — **melayani DUA tombol yang berbeda di layar**, dan itu bukan
   * penyederhanaan kita melainkan bentuk BE-nya.
   *
   * `ProfileEntriesController#destroy` bercabang berdasarkan KEADAAN barisnya, bukan berdasarkan
   * aksi yang diminta klien:
   *
   * ```ruby
   * if @actual_entry && @actual_entry.vendor_registration_form&.revision.nil?
   *   @actual_entry.destroy!   # baris DRAFT  → dihapus sungguhan
   * else
   *   @panel.mark_delete!      # baris TERKUNCI → lahir entry baru ber-entry_type DELETE
   * end
   * ```
   *
   * Dipadukan dengan `index` di controller yang sama (`revision_items` = baris ber-`revision IS
   * NULL` alias draft; `items` = revisi aktif terakhir alias terkunci), maka:
   *
   * | Tombol | `id` yang dikirim | Yang terjadi |
   * |---|---|---|
   * | **Hapus** | `current.id` | `mark_delete!` → muncul revisi `DELETE` tertunda, kartunya tetap tampil |
   * | **Batalkan Perubahan** | `revision.id` | `destroy!` → draftnya lenyap, tinggal baris resmi |
   *
   * ⚠️ Karena itu **`id` yang dikirim adalah satu-satunya pembeda**. Menukar keduanya tidak
   * menghasilkan error apa pun: mengirim `current.id` saat user menekan "Batalkan Perubahan" akan
   * menambah pengajuan hapus, bukan membatalkan apa pun.
   *
   * Ditaruh di service panel BERSAMA, bukan disalin ke 15 service section: `destroy` didefinisikan
   * di `ProfileEntriesController` yang menjadi induk ke-15 panel, jadi bentuknya memang satu.
   *
   * `data` response sengaja `unknown` — pemanggil hanya membutuhkan `notification`, dan mengetik
   * `data` di sini berarti service bersama ini harus tahu 15 bentuk panel.
   *
   * @param endpoint path relatif, mis. `vendor/registration/profile_offices`
   * @param id `current.id` (Hapus) atau `revision.id` (Batalkan Perubahan) — lihat tabel di atas
   * @param vendorId `'current_user'` (situs vendor) atau UUID vendor (situs admin)
   */
  deleteEntry(
    endpoint: string,
    id: string,
    vendorId: string,
  ): Observable<PanelEntryDeleteResponse> {
    return this.http.delete<PanelEntryDeleteResponse>(`${this.base}/${endpoint}/${id}`, {
      params: panelParams(vendorId),
    });
  }
}
